import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const stepsData = {
  labels: [2, 4, 6],
  datasets: [
    {
      label: "ChatGPT Total Steps",
      data: [8, 16, 24],
      backgroundColor: "#D11043"
    },
    {
      label: "UnRead Total Steps (with Edit Context)",
      data: [6, 9, 11],
      backgroundColor: "#F78D29"
    }
  ]
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "top",
      labels: {
        color: "#000000",
        font: { family: "monospace", size: 12 }
      }
    },
    title: {
      display: true,
      text: "Total Steps by Number of Questions",
      color: "#000000",
      font: { family: "monospace", size: 18, weight: "bold" }
    },
    tooltip: {
      titleFont: { family: "monospace" },
      bodyFont: { family: "monospace" },
      callbacks: {
        label: function (context) {
          return `${context.dataset.label}: ${context.parsed.y} steps`;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: "Total Interaction Steps",
        color: "#000000",
        font: { family: "monospace", size: 14 }
      },
      ticks: { color: "#000000", font: { family: "monospace", size: 12 } }
    },
    x: {
      title: {
        display: true,
        text: "Number of Questions",
        color: "#000000",
        font: { family: "monospace", size: 14 }
      },
      ticks: { color: "#000000", font: { family: "monospace", size: 12 } }
    }
  }
};

const contextLeakageComparisonData = {
  labels: ["Correct", "Blocked / Couldn’t Answer", "Leaked / Wrong Context"],
  datasets: [
    {
      label: "UnRead",
      data: [7, 3, 0],
      backgroundColor: "#F78D29"
    },
    {
      label: "ChatGPT",
      data: [4, 0, 6],
      backgroundColor: "#D11043"
    }
  ]
};

const contextLeakageComparisonOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "top",
      labels: { color: "#000000", font: { family: "monospace", size: 14 } }
    },
    title: {
      display: true,
      text: "Context Leakage: UnRead vs ChatGPT",
      color: "#000000",
      font: { family: "monospace", size: 18, weight: "bold" }
    },
    tooltip: {
      titleFont: { family: "monospace" },
      bodyFont: { family: "monospace" },
      callbacks: {
        label: function (context) {
          return `${context.dataset.label}: ${context.parsed.y} tasks`;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: "Number of Tasks",
        color: "#000000",
        font: { family: "monospace", size: 14 }
      },
      ticks: { color: "#000000", font: { family: "monospace", size: 12 }, stepSize: 1 }
    },
    x: {
      title: {
        display: true,
        text: "Response Type",
        color: "#000000",
        font: { family: "monospace", size: 14 }
      },
      ticks: { color: "#000000", font: { family: "monospace", size: 12 } }
    }
  }
};


function FileManager() {
  return (
    <div className="project-page">
      {/* Project Header */}
      <h1 className="github-link">
        <a
          href="https://github.com/Van-Ma/unRead"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Visit unRead on GitHub (opens in a new tab)"
          className="github-icon"
        >
          <i className="bi bi-github" aria-hidden="true"></i>
        </a>
        <span className="github-text">UnRead</span>
      </h1>

      {/* Tech & Role */}
      <div className="tech-tools">
        <p>Figma | React | Python | HuggingFaceAPI</p>
        <div className="project-duration">
          <p><strong>Duration:</strong> ~70 hours</p>
          <p><strong>Role:</strong> Researcher, Designer, Developer</p>
          <p><strong>Team Members:</strong> 2</p>
          <p>
            Traditional AI chats suffer from context leakage and high repetition in academic workflows.

            <br></br>
            <br></br>My solution: A structured context-management system that reduces manual input by over 50% for complex tasks.
          </p>
        </div>
      </div>


      <div className="project-video">
        <video
          src="/workspace/unread-demo.mp4"
          controls
          playsInline
          preload="metadata"
          poster="/workspace/demo5.png"
        />
      </div>

      <div className="project-image">
        <img src="/workspace/demo1.png" alt="UnRead File Manager"></img>
      </div>

      <h2>System Architecture:</h2>
      <ul>
        <li><strong>Transparency:</strong> Context is always visible,
          helping users see what the AI is using and build trust.</li>
        <li><strong>Controlled Scoping:</strong> Context folders define clear
          boundaries, preventing leakage between tasks and keeping responses predictable.</li>
        <li><strong>Editable Context:</strong> Users can add, remove,
          or refine context directly without
          rewriting prompts.</li>
        <li><strong>Traceability:</strong> Session logs preserve context
          and conversation history.</li>
      </ul>

      <h2>Benchmark Testing:</h2>
      <p>To evaluate UnRead’s efficiency, I conducted a structured benchmark comparing it against
        ChatGPT's chat interface. Tasks were predefined with 2, 4, and 6 questions, representing
        small, medium, and sustained workloads.

        Each “interaction step” was counted as a typing action, click, or context edit. For UnRead, this included
        using the two-click “Edit Context” workflow to modify or add context without rewriting prompts.</p>


      <h2>Interaction Cost Model:</h2>

      <div className="bar-chart">
        <Bar data={stepsData} options={chartOptions} />
      </div>

      <p>
        While UnRead introduces a slightly higher initial configuration cost compared to a traditional
        AI chat interface, it significantly reduces marginal interaction cost across iterative tasks.
        This tradeoff was analyzed using a simple workflow cost model:
      </p>

      <p>
        <strong>Total Cost = Setup Cost + (Cost per Question × Number of Questions)</strong>
      </p>

      <p>
        In a traditional AI chat workflow, the initial setup is minimal, but users often need to
        restate or rewrite context for follow-up questions. This increases the cost per additional
        interaction. In contrast, UnRead requires slightly more upfront configuration to define
        structured context, but subsequent questions require only a single interaction step.
      </p>

      <p>
        <strong>Based on benchmark measurements:</strong>
      </p>

      <ul>
        <li>For small tasks (N ≤ 2 questions), traditional chat interfaces are faster due to lower setup cost.</li>
        <li>For iterative tasks (N ≥ 4 questions), UnRead becomes more efficient as marginal interaction cost is reduced.
        </li>
      </ul>

      <p>
        UnRead demonstrates that making AI context visible, structured, and
        editable can significantly improve efficiency in iterative workflows.
        Benchmark testing shows that for small tasks, ChatGPT's interface
        remained slightly faster, but for medium and sustained tasks, UnRead reduces total
        interaction steps by 44–54%, streamlining task setup and minimizing repetitive edits.
        These results highlight the value of structured context management in supporting sustained,
        multi-step academic or professional workflows, increasing predictability and reducing user effort.
      </p>


      <div className="results-impact">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Condition</th>
                <th>Questions (N)</th>
                <th>ChatGPT Total Steps</th>
                <th>UnRead Total Steps</th>
                <th>Faster System</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Small</td>
                <td>2</td>
                <td>8</td>
                <td>6</td>
                <td>ChatGPT</td>
              </tr>
              <tr>
                <td>Medium</td>
                <td>4</td>
                <td>16</td>
                <td>9</td>
                <td>UnRead</td>
              </tr>
              <tr>
                <td>Sustained</td>
                <td>6</td>
                <td>24</td>
                <td>11</td>
                <td>UnRead</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <h2>Context Leakage Test: UnRead vs ChatGPT</h2>
      <p>
        To test context leakage, 10 tasks were conducted where questions about one file followed interactions with a separate, unrelated file.
        Responses were categorized as:
      </p>
      <ul>
        <li><strong>Correct:</strong> Only referenced the relevant file/context</li>
        <li><strong>Blocked / Couldn’t Answer:</strong> Responded with “I can’t answer that” when necessary information was missing</li>
        <li><strong>Leaked / Wrong Context:</strong> Referenced irrelevant context from other files or past conversations</li>
      </ul>

      <div className="bar-chart">
        <Bar data={contextLeakageComparisonData} options={contextLeakageComparisonOptions} />
      </div>

      <div className="results-impact">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>System</th>
                <th>Correct</th>
                <th>Blocked / Couldn’t Answer</th>
                <th>Leaked / Wrong Context</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>UnRead</td>
                <td>7/10</td>
                <td>3/10</td>
                <td>0/10</td>
              </tr>
              <tr>
                <td>ChatGPT</td>
                <td>4/10</td>
                <td>0/10</td>
                <td>6/10</td>
              </tr>
            </tbody>
          </table>

        </div>

      </div>

      <p>
        This comparison highlights that UnRead’s structured and editable context effectively
        prevents context leakage, whereas ChatGPT frequently referenced unrelated information
        or prior conversations. In UnRead, when necessary information was missing, the system
        declined to answer. Responses from UnRead proved consistently
        accurate to the provided source, making it particularly suitable for academic or
        professional workflows where accuracy and consistency are critical. By strictly
        using only the intended context, UnRead minimizes the risk of incorporating incorrect
        or irrelevant information, making it especially valuable for tasks such as literature
        reviews, data analysis, or collaborative research where reliability is essential.
      </p>

      <h2>Recommendations:</h2>
      <ul>
        <li>Implement editable context folders in iterative AI workflows to reduce interaction steps and prevent context leakage.
          This is especially valuable for tasks requiring multi-step reasoning, like research or analysis.</li>
        <span className="img-descript">↑ This improves efficiency for multi-step tasks and ensures reliable outputs.</span>
        <li>Introduce optional onboarding prompts or template context setups to lower the initial configuration cost while maintaining the benefits of structured context.</li>
        <span className="img-descript">↑ This reduces friction for new users while preserving accuracy.</span>
        <li>Apply interaction step counts and context leakage tests as standard KPIs when iterating on AI interface features or designing new workflow tools.</li>
        <span className="img-descript">↑ This provides clear, data-driven guidance for UX and product decisions.</span>
      </ul>

      <p className="disclaimer">
        <strong>Note:</strong> Following a collaborative research phase on LLM capabilities, I independently spearheaded the design and development of the platform. I owned the full product lifecycle, including UI/UX design, full-stack implementation, system testing, and benchmark testing.
      </p>

    </div>
  );
};

export default FileManager;
